"use client";

import { useState, type FormEvent } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

type Status = { kind: "idle" | "sending" | "sent" } | { kind: "error"; message: string };

export default function FeatureSuggestionForm() {
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    setStatus({ kind: "sending" });
    try {
      const response = await fetch("/api/feature-suggestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "Your suggestion could not be sent. Try again shortly.");
      form.reset();
      setStatus({ kind: "sent" });
    } catch (error) {
      setStatus({ kind: "error", message: error instanceof Error ? error.message : "Your suggestion could not be sent." });
    }
  };

  const sending = status.kind === "sending";
  return (
    <form onSubmit={submit} className="grid gap-5" noValidate={false}>
      {/* Left empty by people; bots that fill every field are dropped. */}
      <div className="hidden" aria-hidden="true">
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="suggestion-name">Name</Label>
          <Input id="suggestion-name" name="name" required minLength={2} maxLength={100} autoComplete="name" />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="suggestion-email">
            Email <span className="font-normal text-muted-foreground">(optional, if you would like a reply)</span>
          </Label>
          <Input id="suggestion-email" name="email" type="email" maxLength={200} autoComplete="email" />
        </div>
      </div>
      <fieldset className="grid gap-2">
        <legend className="mb-2 text-sm font-medium">Phone</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {["iPhone", "Android", "Both"].map((platform, index) => (
            <label key={platform} className="inline-flex items-center gap-2">
              <input type="radio" name="platform" value={platform} defaultChecked={index === 2} className="h-4 w-4 accent-current" />
              {platform}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-2">
        <Label htmlFor="suggestion-message">Suggestion</Label>
        <Textarea
          id="suggestion-message"
          name="message"
          required
          minLength={10}
          maxLength={4000}
          rows={6}
          placeholder="What would you like Stone Mobile to do, and what would you use it for?"
        />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" disabled={sending}>{sending ? "Sending…" : "Send suggestion"}</Button>
        <p role="status" className="text-sm text-muted-foreground">
          {status.kind === "sent" ? "Suggestion sent. Thank you." : status.kind === "error" ? status.message : ""}
        </p>
      </div>
    </form>
  );
}
